if (!customElements.get('grofie-collection-loader')) {
  customElements.define('grofie-collection-loader', class extends HTMLElement {
    connectedCallback() {
      if (this.observer || !('IntersectionObserver' in window)) return;
      this.root = this.closest('.gplp');
      this.grid = this.root?.querySelector('.gplp__grid');
      this.pagination = this.root?.querySelector('[data-collection-pagination]');
      this.status = this.querySelector('[data-load-status]');
      this.spinner = this.querySelector('.gplp__autoload-spinner');
      this.retry = this.querySelector('[data-load-retry]');
      if (!this.grid || !this.pagination) return;
      this.loading = false;
      this.failed = false;
      this.controller = new AbortController();
      this.seenProducts = new Set();
      this.loadedPages = new Set();
      this.soldOutCards = [];
      const availability = new URL(location.href).searchParams.getAll('filter.v.availability');
      this.stockLast = !(availability.length === 1 && availability[0] === '0');
      this.stageCards(Array.from(this.grid.children));
      this.retry.addEventListener('click', () => {
        this.failed = false;
        this.loadNext();
      }, { signal: this.controller.signal });
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.loadNext();
      }, { rootMargin: '0px 0px 300px 0px' });
      this.pagination.hidden = true;
      this.hidden = false;
      if (this.dataset.nextUrl) this.observer.observe(this);
      else this.finish();
    }

    stageCards(cards) {
      const stocked = [];
      for (const card of cards) {
        const id = card.dataset.productId;
        if (id && this.seenProducts.has(id)) {
          card.remove();
          continue;
        }
        if (id) this.seenProducts.add(id);
        if (this.stockLast && card.matches('[data-sold-out="true"], .rs-card--sold-out')) {
          card.remove();
          this.soldOutCards.push(card);
        } else stocked.push(card);
      }
      this.grid.append(...stocked);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.controller?.abort();
      if (this.soldOutCards?.length) this.grid?.append(...this.soldOutCards.splice(0));
    }

    finish() {
      this.observer?.disconnect();
      this.grid.append(...this.soldOutCards.splice(0));
      this.spinner.hidden = true;
      this.retry.hidden = true;
      this.status.textContent = this.dataset.endText;
    }

    async loadNext() {
      if (this.loading || this.failed || !this.dataset.nextUrl || !this.isConnected) return;
      this.loading = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.spinner.hidden = false;
      this.retry.hidden = true;
      this.status.textContent = this.dataset.loadingText;
      try {
        const url = new URL(this.dataset.nextUrl, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error('Unexpected collection URL');
        const current = new URL(location.href);
        for (const key of new Set(current.searchParams.keys())) {
          if (key !== 'page' && key !== 'section_id' && key !== 'sections' && !url.searchParams.has(key)) {
            current.searchParams.getAll(key).forEach(value => url.searchParams.append(key, value));
          }
        }
        url.searchParams.set('section_id', this.dataset.sectionId);
        if (this.loadedPages.has(url.href)) throw new Error('Repeated collection page');
        const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        const html = await response.text();
        if (!this.isConnected || this.controller.signal.aborted) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const returned = documentFragment.getElementById(this.root.id);
        const nextGrid = returned?.querySelector('.gplp__grid');
        const nextLoader = returned?.querySelector('grofie-collection-loader');
        const cards = Array.from(nextGrid?.children || []);
        if (!cards.length || !nextLoader) throw new Error('Missing collection products');
        const nextUrl = nextLoader.dataset.nextUrl;
        if (nextUrl === this.dataset.nextUrl) throw new Error('Repeated collection page');
        this.stageCards(cards);
        this.loadedPages.add(url.href);
        this.dataset.nextUrl = nextUrl;
        this.status.textContent = '';
        if (!nextUrl) this.finish();
        else {
          this.observer.unobserve(this);
          this.observer.observe(this);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.failed = true;
          this.status.textContent = this.dataset.errorText;
          this.retry.hidden = false;
        }
      } finally {
        this.loading = false;
        this.grid.removeAttribute('aria-busy');
        this.spinner.hidden = true;
      }
    }
  });
}
